/**
 * The list which opens after an at sign in a post.
 *
 * The at sign alone already opens it, the way a select opens on a click:
 * the people of the thread being answered first, then whoever was on the
 * portal last. Every letter typed after it narrows the list to the accounts
 * which begin with them, drawn the same way every other list of people on
 * the portal is: the face, then the name. Picking one writes the whole name
 * into the post; carrying on typing ignores the list, which is always allowed
 * - the list is a shortcut to spelling a name, never the only way to.
 *
 * What ends up in the post is text. "@Henn" stays "@Henn" in what was saved,
 * and it is the page which turns it into a link with a face, so a name typed
 * by hand and a name picked from the list come out exactly the same.
 *
 * The list stands at the caret rather than under the box. A box of twenty
 * lines with a list at the bottom of it is a list somebody has to go looking
 * for, so where the caret is on the screen is worked out by laying the text
 * out again in a hidden copy of the box - the one way a browser will say
 * where a character in a textarea actually is.
 */

import { query, queryAll } from "../core/dom";
import { getJson } from "../core/http";
import { text } from "../core/i18n";

/** How long typing has to pause before the list is asked for. */
const PAUSE_MS = 140;

/**
 * The name being typed after the at sign, right up against the caret.
 *
 * Nothing at all after it counts too: the list opens on the at sign itself.
 * Not after a letter or a digit, though - an address being typed is not
 * somebody being pointed at.
 */
const TYPING = /(?:^|[\s(>"'])@([A-Za-z0-9_]{0,16})$/;

/** One account the list offers. */
interface Candidate {
    readonly username: string;
    readonly face: string;
}

/** What the endpoint answers. */
interface CandidateList {
    readonly items: readonly Candidate[];
}

/**
 * Return the name being typed before the caret of *field*, and where it starts.
 *
 * `null` when the caret is not straight after "@" and a run of letters - in
 * the middle of an address, after a space, or anywhere a name is not being
 * spelled.
 */
export function typedName(field: HTMLTextAreaElement): { name: string; start: number } | null {
    const caret = field.selectionStart ?? 0;

    if (caret !== (field.selectionEnd ?? caret)) {
        return null;
    }

    const before = field.value.slice(0, caret);
    const found = TYPING.exec(before);

    if (!found || found[1] === undefined) {
        return null;
    }

    return { name: found[1], start: caret - found[1].length - 1 };
}

/** What may follow a name straight away, with no space in between. */
const FOLLOWS_A_NAME = /^[\s,.!?;:)\]"']/;

/**
 * Write *name* over what was typed after the at sign, and a space after it.
 *
 * The space is what lets somebody carry straight on writing the sentence the
 * name was part of, rather than having to type one first. Not when the
 * sentence already carries on - a comma or a space right after the name is
 * the sentence, and a space in front of it would be a typo nobody made.
 */
export function writeName(field: HTMLTextAreaElement, start: number, name: string): void {
    const caret = field.selectionStart ?? field.value.length;
    const rest = field.value.slice(caret);
    const written = FOLLOWS_A_NAME.test(rest) ? `@${name}` : `@${name} `;

    field.value = field.value.slice(0, start) + written + rest;
    field.selectionStart = field.selectionEnd = start + written.length;
    field.dispatchEvent(new Event("input", { bubbles: true }));
}

/** Return where the caret of *field* is on the screen, below its line. */
function caretPoint(field: HTMLTextAreaElement, at: number): { left: number; top: number } {
    const style = window.getComputedStyle(field);
    const mirror = document.createElement("div");

    // Everything which decides where a line wraps, copied from the box.
    for (const property of [
        "boxSizing",
        "width",
        "paddingTop",
        "paddingRight",
        "paddingBottom",
        "paddingLeft",
        "borderTopWidth",
        "borderLeftWidth",
        "fontFamily",
        "fontSize",
        "fontWeight",
        "lineHeight",
        "letterSpacing",
        "wordSpacing",
        "tabSize",
    ] as const) {
        mirror.style[property] = style[property];
    }

    mirror.style.position = "absolute";
    mirror.style.visibility = "hidden";
    mirror.style.whiteSpace = "pre-wrap";
    mirror.style.overflowWrap = "break-word";
    mirror.textContent = field.value.slice(0, at);

    const mark = document.createElement("span");
    mark.textContent = "​";
    mirror.appendChild(mark);
    document.body.appendChild(mirror);

    const box = field.getBoundingClientRect();
    const line = Number.parseFloat(style.lineHeight) || 20;
    const point = {
        left: box.left + mark.offsetLeft - field.scrollLeft,
        top: box.top + mark.offsetTop - field.scrollTop + line,
    };

    mirror.remove();

    return point;
}

/** Wire the list of one editor. */
export function bindMentionPicker(container: HTMLElement): void {
    const field = query<HTMLTextAreaElement>("[data-editor-textarea]", container);
    const endpoint = container.dataset["mentionsUrl"] ?? "";

    if (!field || !endpoint) {
        return;
    }

    const menu = document.createElement("div");
    menu.className = "select__menu mention-menu";
    menu.hidden = true;

    // What the list is and how it is used, above and below the people in it.
    const heading = document.createElement("div");
    heading.className = "mention-menu__head";
    heading.textContent = text("js.mention.label", "Označiť hráča");

    const options = document.createElement("div");
    options.className = "mention-menu__options";
    options.setAttribute("role", "listbox");
    options.setAttribute("aria-label", text("js.mention.label", "Označiť hráča"));

    const hint = document.createElement("div");
    hint.className = "mention-menu__hint";
    hint.textContent = text("js.mention.hint", "↑↓ vybrať · Enter vložiť · Esc zavrieť");

    menu.append(heading, options, hint);
    document.body.appendChild(menu);

    let candidates: readonly Candidate[] = [];
    let active = 0;
    let asking: number | undefined;
    let typed: { name: string; start: number } | null = null;

    const close = (): void => {
        menu.hidden = true;
        candidates = [];
        typed = null;
    };

    const highlight = (index: number): void => {
        active = index;
        for (const [position, row] of queryAll<HTMLElement>(".select__option", options).entries()) {
            row.classList.toggle("is-active", position === index);
            row.setAttribute("aria-selected", String(position === index));
        }
    };

    const pick = (index: number): void => {
        const chosen = candidates[index];
        if (!chosen || !typed) {
            return;
        }

        writeName(field, typed.start, chosen.username);
        close();
        field.focus();
    };

    const draw = (): void => {
        options.replaceChildren();

        if (candidates.length === 0 || !typed) {
            close();

            return;
        }

        for (const [index, candidate] of candidates.entries()) {
            const row = document.createElement("div");
            row.className = "select__option";
            row.setAttribute("role", "option");

            const face = document.createElement("img");
            face.className = "select__face";
            face.src = candidate.face;
            face.width = 18;
            face.height = 18;
            face.alt = "";

            const name = document.createElement("span");
            name.className = "select__label";
            name.textContent = candidate.username;

            row.append(face, name);

            // Pressed rather than clicked, so the box never loses the caret
            // before the name is written into it.
            row.addEventListener("mousedown", (event: MouseEvent) => {
                event.preventDefault();
                pick(index);
            });

            options.appendChild(row);
        }

        const point = caretPoint(field, typed.start);
        menu.style.left = `${Math.round(Math.min(point.left, window.innerWidth - 240))}px`;
        menu.style.top = `${Math.round(point.top)}px`;
        menu.hidden = false;
        highlight(0);
    };

    const ask = (): void => {
        window.clearTimeout(asking);
        typed = typedName(field);

        if (!typed) {
            close();

            return;
        }

        const wanted = typed.name;
        asking = window.setTimeout(() => {
            const joiner = endpoint.includes("?") ? "&" : "?";

            void getJson<CandidateList>(`${endpoint}${joiner}q=${encodeURIComponent(wanted)}`)
                .then((payload) => {
                    // Something else may have been typed while this was out.
                    if (typed?.name !== wanted) {
                        return;
                    }

                    candidates = payload.items;
                    draw();
                })
                .catch(close);
        }, PAUSE_MS);
    };

    field.addEventListener("input", ask);
    field.addEventListener("click", ask);
    field.addEventListener("blur", () => window.setTimeout(close, 120));

    // The list is pinned to the window, so a page which moves under it would
    // leave it pointing at a line which is no longer there.
    window.addEventListener(
        "scroll",
        () => {
            if (!menu.hidden) {
                close();
            }
        },
        { passive: true },
    );

    field.addEventListener("keydown", (event: KeyboardEvent) => {
        if (menu.hidden || candidates.length === 0) {
            return;
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            const step = event.key === "ArrowDown" ? 1 : -1;
            highlight((active + step + candidates.length) % candidates.length);
        } else if (event.key === "Enter" || event.key === "Tab") {
            event.preventDefault();
            pick(active);
        } else if (event.key === "Escape") {
            // The list goes and the name typed so far stays: escaping the
            // suggestion is not deleting what was written.
            event.preventDefault();
            close();
        }
    });
}

/** Wire every editor of the current page which may point at people. */
export function initMentionPickers(): void {
    for (const container of queryAll<HTMLElement>("[data-editor][data-mentions-url]")) {
        bindMentionPicker(container);
    }
}
